// Import React hooks and components for the Admin Dashboard component
// Enhanced with responsive design, consistent card dimensions, and improved visual hierarchy
import React, { useState, useEffect } from 'react';
import Cookies from 'js-cookie';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Avatar,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  Menu,
  ListItemIcon,
} from '@mui/material';
import SecureImage from '../Common/SecureImage';
import SecureVideo from '../Common/SecureVideo';
import AiFailsafeStatusChip from '../Common/AiFailsafeStatusChip';
import {
  People as PeopleIcon,
  Security as SecurityIcon,
  Business as BusinessIcon,
  History as HistoryIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Videocam as VideocamIcon,
  Image as ImageIcon,
  PlayArrow as PlayArrowIcon,
  Download as DownloadIcon,
  Speed as SpeedIcon,
  Timer as TimerIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
  FileDownload as FileDownloadIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  Description as DescriptionIcon,
  InsertChart as InsertChartIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../contexts/PermissionContext';
import { dashboardAPI, generationReportsAPI } from '../../services/api';
import { API_URLS } from '../../config/apiConfig';
import { getThumbnailUrl as getSecureThumbnailUrl, getSecureVideoStreamUrl } from '../../utils/imageUrlUtils';


const StatCard = ({ title, value, icon, color = 'primary' }) => (
  <Card
    sx={{
      height: '100%',
      minHeight: { xs: 80, sm: 85, md: 100 },
      display: 'flex',
      flexDirection: 'column',
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
      }
    }}
  >
    <CardContent
      sx={{
        p: { xs: 1.25, sm: 1.5, md: 1.5 },
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center'
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            color="text.secondary"
            variant="h6"
            sx={{
              fontSize: { xs: '0.7rem', sm: '0.75rem', md: '0.85rem' },
              fontWeight: 600,
              lineHeight: 1.2,
              mb: 0.25
            }}
          >
            {title}
          </Typography>
          <Typography
            variant="h4"
            sx={{
              fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' },
              fontWeight: 800,
              lineHeight: 1,
              color: 'text.primary'
            }}
          >
            {value}
          </Typography>
        </Box>
        <Avatar
          sx={{
            bgcolor: `${color}.main`,
            width: { xs: 32, sm: 36, md: 40 },
            height: { xs: 32, sm: 36, md: 40 },
            ml: 1.5,
            flexShrink: 0,
            boxShadow: `0 2px 8px ${color}.main}30`
          }}
        >
          {React.cloneElement(icon, { sx: { fontSize: { xs: 18, sm: 20, md: 22 } } })}
        </Avatar>
      </Box>
    </CardContent>
  </Card>
);

const Dashboard = () => {
  const { user, isAuthenticated } = useAuth();
  const { hasPermission } = usePermissions();
  const [stats, setStats] = useState({
    users: 0,
    roles: 0,
    departments: 0,
    auditLogs: 0,
    today_videos: 0,
    today_images: 0,
  });
  const [recentActivities, setRecentActivities] = useState([]);
  const [metabaseUrl, setMetabaseUrl] = useState(''); // State for Metabase public link
  const [loading, setLoading] = useState(true);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  // Generation Performance state
  const [reportPeriod, setReportPeriod] = useState('7d');
  const [reportType, setReportType] = useState('all');
  const [perfData, setPerfData] = useState(null);
  const [failureData, setFailureData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [csvDownloading, setCsvDownloading] = useState(false);

  // Media preview state
  const [mediaPreview, setMediaPreview] = useState({ open: false, url: '', title: '', isVideo: false, generationId: null });

  // Helper to append dynamic filters to Metabase URL
  const getDynamicMetabaseUrl = (url) => url || '';

  // Helper to build filter params for Metabase JWT endpoint
  const buildMetabaseParams = (period, type) => {
    const params = {};
    if (type && type !== 'all') {
      params.gen_type = type === 'image' ? 'IMAGE_TO_IMAGE' : 'IMAGE_TO_VIDEO';
    }
    if (period && period !== 'lifetime') {
      params.period = period; // backend maps '7d', '30d', '1d'
    }
    return params;
  };

  // Fetch Metabase embed URL with current filters baked into JWT
  const fetchMetabaseEmbed = (period, type) => {
    const params = buildMetabaseParams(period, type);
    generationReportsAPI.getMetabaseEmbedUrl(params)
      .then(res => {
        if (res.data?.embed_url) setMetabaseUrl(res.data.embed_url);
      })
      .catch(err => console.error('Metabase embed URL error:', err));
  };

  useEffect(() => {
    if (isAuthenticated() && user) {
      loadDashboardData();
      loadReportData('7d', 'all');
      fetchMetabaseEmbed('7d', 'all'); // initial load with default filters
    }
  }, [isAuthenticated, user]);

  // Re-fetch Metabase embed URL whenever filters change (bakes new filters into JWT)
  useEffect(() => {
    if (isAuthenticated() && user) {
      fetchMetabaseEmbed(reportPeriod, reportType);
    }
  }, [reportPeriod, reportType]);

  const loadDashboardData = async () => {
    try {
      // Use the new dashboard API to get accurate stats
      // This API ensures only active records (status = 1) are counted
      // and applies role-based filtering (superadmin sees all companies, others see only their company)
      const dashboardResponse = await dashboardAPI.getStats();

      if (dashboardResponse.data.success) {
        const { stats: dashboardStats, recent_activities, is_superadmin } = dashboardResponse.data;

        setStats({
          users: dashboardStats.users || 0,
          roles: dashboardStats.roles || 0,
          departments: dashboardStats.departments || 0,
          auditLogs: dashboardStats.audit_logs || 0,
          today_videos: dashboardStats.today_videos || 0,
          today_images: dashboardStats.today_images || 0,
        });

        // Set recent activities
        setRecentActivities(recent_activities || []);

        // Debug: Log the structure of recent activities
        if (recent_activities && recent_activities.length > 0) {
          console.log('Recent activities structure:', recent_activities[0]);
        }

        // Set superadmin status for UI display
        setIsSuperAdmin(is_superadmin || false);
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get secure thumbnail/preview URL for media
  const getSecureThumbnailPreview = (generationId, isVideo, type = null) => {
    if (isVideo) {
      // For videos, use the secure video stream URL instead of image thumbnail
      return getSecureVideoStreamUrl(generationId);
    }
    // For images, use standardized 1200x1200px thumbnails for dashboard previews
    return getSecureThumbnailUrl(generationId, '1200x1200', type);
  };

  // Open media preview popup
  const openMediaPreview = (generationId, title, isVideo, type = null) => {
    const secureUrl = getSecureThumbnailPreview(generationId, isVideo, type);

    // Debug: Log the media preview details
    console.log('Opening media preview:', {
      generationId,
      title,
      isVideo,
      type,
      secureUrl
    });

    setMediaPreview({ open: true, url: secureUrl, title, isVideo, generationId });
  };

  // Close media preview popup
  const closeMediaPreview = () => {
    setMediaPreview({ open: false, url: '', title: '', isVideo: false, generationId: null });
  };

  // Download file function - Downloads file instead of opening in new tab
  const downloadFile = async (url, filename) => {
    try {
      // Determine authentication token based on context
      const isAdminPortal = window.location.pathname.startsWith('/glam-admin/');
      let token;
      
      if (isAdminPortal) {
        token = localStorage.getItem('access_token') || Cookies.get('access_token');
      } else {
        token = Cookies.get('merchant_access_token');
      }

      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(url, { headers });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the URL object
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Download failed:', error);
      // Fallback to opening in new tab if download fails
      window.open(url, '_blank');
    }
  };

  // --- Generation Reports ---
  const loadReportData = async (period, type) => {
    setReportLoading(true);
    try {
      const periodKey = period === '1d' ? 'last_1d' : period === '7d' ? 'last_7d' : period === '30d' ? 'last_30d' : 'lifetime';
      const genTypeParam = type === 'image' ? 'IMAGE_TO_IMAGE' : type === 'video' ? 'IMAGE_TO_VIDEO' : undefined;

      const [perfResp, failResp] = await Promise.all([
        generationReportsAPI.getPerformanceSummary(genTypeParam ? { generation_type: genTypeParam } : {}),
        generationReportsAPI.getFailureBreakdown({ period, ...(genTypeParam ? { generation_type: genTypeParam } : {}) }),
      ]);

      if (perfResp.data?.periods) {
        setPerfData(perfResp.data.periods[periodKey] || null);
      }
      if (failResp.data) {
        setFailureData(failResp.data);
      }
    } catch (error) {
      console.error('Error loading report data:', error);
    } finally {
      setReportLoading(false);
    }
  };

  const handlePeriodChange = (_, newPeriod) => {
    if (newPeriod) {
      setReportPeriod(newPeriod);
      loadReportData(newPeriod, reportType);
    }
  };

  const handleTypeChange = (e) => {
    const newType = e.target.value;
    setReportType(newType);
    loadReportData(reportPeriod, newType);
  };

  const handleCsvDownload = async () => {
    setCsvDownloading(true);
    try {
      const genTypeParam = reportType === 'image' ? 'IMAGE_TO_IMAGE' : reportType === 'video' ? 'IMAGE_TO_VIDEO' : undefined;
      const response = await generationReportsAPI.exportCSV({
        period: reportPeriod,
        ...(genTypeParam ? { generation_type: genTypeParam } : {}),
      });
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `generation_report_${reportPeriod}_${reportType}_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('CSV download failed:', error);
    } finally {
      setCsvDownloading(false);
    }
  };

  const [summaryDownloading, setSummaryDownloading] = useState(false);
  const handleDailySummaryDownload = async () => {
    setSummaryDownloading(true);
    try {
      const response = await generationReportsAPI.exportSummaryCSV({ period: reportPeriod });
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `daily_summary_${reportPeriod}_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Daily summary download failed:', error);
    } finally {
      setSummaryDownloading(false);
    }
  };


  const [anchorEl, setAnchorEl] = useState(null);
  const openMenu = Boolean(anchorEl);
  const handleExportClick = (event) => setAnchorEl(event.currentTarget);
  const handleExportClose = () => setAnchorEl(null);

  const handleDownloadDetails = () => {
    handleExportClose();
    handleCsvDownload();
  };

  const handleDownloadSummary = () => {
    handleExportClose();
    handleDailySummaryDownload();
  };

  const formatMs = (ms) => {
    if (!ms || ms === 0) return '—';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  const getActivityIcon = (action) => {
    return (action && action.toLowerCase().includes('create')) ?
      <CheckCircleIcon color="success" /> :
      <ErrorIcon color="error" />;
  };

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      {/* Header Section */}
      <Box sx={{ mb: { xs: 2, sm: 2.5, md: 3 } }}>
        <Typography
          variant="h4"
          gutterBottom
          sx={{
            fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
            fontWeight: 700,
            color: 'text.primary',
            lineHeight: 1.2,
            mb: 1
          }}
        >
          Welcome back, {user && user.name}! 👋
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              fontSize: { xs: '0.875rem', sm: '1rem' },
              lineHeight: 1.4,
              maxWidth: '700px'
            }}
          >
            {isSuperAdmin ? (
              "Here's what's happening across all organizations in the system"
            ) : (
              `Here's what's happening in your ${user && user.company_name} organization`
            )}
          </Typography>
          {isSuperAdmin && (
            <Chip
              label="Super Admin Access"
              color="primary"
              variant="outlined"
              size="small"
              sx={{ fontWeight: 600 }}
            />
          )}
        </Box>
      </Box>

      {/* Unified Header Stats: System Overview + Today's Activity */}
      <Box sx={{ mb: { xs: 1.5, sm: 2, md: 2 } }}>
        <Grid container spacing={{ xs: 1, sm: 1.5, md: 2 }}>
          {/* Left Column: System Overview */}
          <Grid item xs={12} md={8}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                mb: 1.5,
                fontWeight: 600,
                color: 'text.primary',
                fontSize: { xs: '1rem', sm: '1.125rem' }
              }}
            >
              System Overview
            </Typography>
            <Grid container spacing={{ xs: 1, sm: 1.5, md: 2 }}>
              <Grid item xs={6} sm={3}>
                <StatCard
                  title="Total Users"
                  value={stats.users}
                  icon={<PeopleIcon />}
                  color="primary"
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatCard
                  title="Roles"
                  value={stats.roles}
                  icon={<SecurityIcon />}
                  color="secondary"
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatCard
                  title="Departments"
                  value={stats.departments}
                  icon={<BusinessIcon />}
                  color="success"
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <StatCard
                  title="Audit Logs"
                  value={stats.auditLogs}
                  icon={<HistoryIcon />}
                  color="warning"
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Right Column: Today's Activity */}
          <Grid item xs={12} md={4}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                mb: 1.5,
                fontWeight: 600,
                color: 'text.primary',
                fontSize: { xs: '1rem', sm: '1.125rem' }
              }}
            >
              Today's Activity
            </Typography>
            <Grid container spacing={{ xs: 1, sm: 1.5, md: 2 }}>
              <Grid item xs={6} md={6}>
                <StatCard
                  title="Today's Videos"
                  value={stats.today_videos}
                  icon={<VideocamIcon />}
                  color="info"
                />
              </Grid>
              <Grid item xs={6} md={6}>
                <StatCard
                  title="Today's Images"
                  value={stats.today_images}
                  icon={<ImageIcon />}
                  color="success"
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>

      {/* Premium Analytics Suite: Unified Generation Performance & Analytical Insights */}
      <Box sx={{ 
        bgcolor: 'grey.50', 
        borderRadius: 4, 
        p: { xs: 1.5, sm: 2, md: 2.5 }, 
        border: '1px solid', 
        borderColor: 'grey.200',
        mb: { xs: 2.5, sm: 3, md: 3.5 }
      }}>
        {/* Suite Header: Minimalist Controls */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, mb: 1.25 }}>
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: 'text.primary', letterSpacing: '-0.01em', lineHeight: 1 }}>
              Generation Performance
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.disabled', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: '0.6rem' }}>
              System-wide AI processing metrics
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            <ToggleButtonGroup
              value={reportPeriod}
              exclusive
              onChange={handlePeriodChange}
              size="small"
              sx={{ 
                height: 28, 
                bgcolor: 'white',
                '& .MuiToggleButton-root': { 
                  px: 1.25, 
                  fontSize: '0.7rem', 
                  fontWeight: 700,
                  border: '1px solid',
                  borderColor: 'grey.200',
                  color: 'text.secondary',
                  '&.Mui-selected': { bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' } }
                }
              }}
            >
              <ToggleButton value="1d">1D</ToggleButton>
              <ToggleButton value="7d">7D</ToggleButton>
              <ToggleButton value="30d">30D</ToggleButton>
              <ToggleButton value="lifetime">ALL</ToggleButton>
            </ToggleButtonGroup>

            <FormControl size="small" sx={{ minWidth: 90 }}>
              <Select
                value={reportType}
                onChange={handleTypeChange}
                sx={{ 
                  height: 28, fontSize: '0.7rem', fontWeight: 700, bgcolor: 'white',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'grey.200' }
                }}
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="image">Image</MenuItem>
                <MenuItem value="video">Video</MenuItem>
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              size="small"
              startIcon={csvDownloading || summaryDownloading ? <CircularProgress size={12} color="inherit" /> : <FileDownloadIcon sx={{ fontSize: '1rem !important' }} />}
              onClick={handleExportClick}
              disabled={csvDownloading || summaryDownloading}
              sx={{ 
                height: 28, fontSize: '0.7rem', fontWeight: 700, borderColor: 'primary.light', color: 'primary.main', px: 1.5,
                bgcolor: 'white', '&:hover': { bgcolor: 'primary.50', borderColor: 'primary.main' }
              }}
            >
              Export
            </Button>
            <Menu
              anchorEl={anchorEl}
              open={openMenu}
              onClose={handleExportClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{ sx: { mt: 1, minWidth: 160, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', borderRadius: 2 } }}
            >
              <MenuItem onClick={handleDownloadDetails} sx={{ py: 1 }}>
                <ListItemIcon><DescriptionIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Detailed Logs" primaryTypographyProps={{ variant: 'caption', fontWeight: 700 }} />
              </MenuItem>
              <MenuItem onClick={handleDownloadSummary} sx={{ py: 1 }}>
                <ListItemIcon><InsertChartIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary="Daily Summary" primaryTypographyProps={{ variant: 'caption', fontWeight: 700 }} />
              </MenuItem>
            </Menu>
          </Box>
        </Box>

        <Box sx={{ mb: 2 }}>
          <AiFailsafeStatusChip variant="card" pollIntervalMs={30000} />
        </Box>

        {reportLoading ? (
          <Box display="flex" justifyContent="center" p={4}><CircularProgress size={32} /></Box>
        ) : perfData ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Stats Row */}
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={6} md={4}>
                <Card sx={{ 
                  borderRadius: 2.5, 
                  border: '1px solid', 
                  borderColor: 'divider', 
                  boxShadow: 'none',
                  height: '100%',
                  minHeight: 110,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center'
                }}>
                  <CardContent sx={{ p: 1.5 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Generations</Typography>
                      <TrendingUpIcon sx={{ fontSize: 16, color: 'primary.main', opacity: 0.7 }} />
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 900, fontFamily: 'monospace', color: 'text.primary', lineHeight: 1, mb: 1 }}>
                      {perfData.total_generations}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ flex: 1, height: 4, borderRadius: 2, bgcolor: 'grey.100', overflow: 'hidden' }}>
                        <Box sx={{ width: `${perfData.success_rate}%`, height: '100%', bgcolor: 'primary.main' }} />
                      </Box>
                      <Typography variant="caption" sx={{ fontWeight: 800, color: 'primary.main', fontFamily: 'monospace' }}>{perfData.success_rate}%</Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Card sx={{ 
                  borderRadius: 2.5, 
                  border: '1px solid', 
                  borderColor: 'divider', 
                  boxShadow: 'none',
                  height: '100%',
                  minHeight: 110,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center'
                }}>
                  <CardContent sx={{ p: 1.5 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Failures</Typography>
                      <WarningIcon sx={{ fontSize: 16, color: perfData.failed > 0 ? 'error.main' : 'success.main', opacity: 0.7 }} />
                    </Box>
                    <Typography variant="h5" sx={{ fontWeight: 900, fontFamily: 'monospace', color: perfData.failed > 0 ? 'error.main' : 'success.main', lineHeight: 1, mb: 1 }}>
                      {perfData.failed}
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.65rem' }}>
                      {perfData.failed === 0 ? 'STATUS: HEALTHY' : `${Math.round((perfData.failed / perfData.total_generations) * 100)}% FAILURE RATE`}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card sx={{ 
                  borderRadius: 2.5, 
                  border: '1px solid', 
                  borderColor: 'divider', 
                  boxShadow: 'none',
                  height: '100%',
                  minHeight: 110,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center'
                }}>
                  <CardContent sx={{ p: 1.5 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                      <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Processing Speed</Typography>
                      <SpeedIcon sx={{ fontSize: 16, color: 'info.main', opacity: 0.7 }} />
                    </Box>
                    {reportType === 'all' ? (
                      <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <Box>
                          <Typography variant="caption" sx={{ display: 'block', color: 'text.disabled', fontWeight: 800, fontSize: '0.55rem' }}>IMAGE AVG</Typography>
                          <Typography variant="subtitle1" sx={{ fontWeight: 900, fontFamily: 'monospace', lineHeight: 1 }}>
                            {perfData.by_generation_type?.IMAGE?.avg_inference_ms ? formatMs(perfData.by_generation_type.IMAGE.avg_inference_ms) : '—'}
                          </Typography>
                        </Box>
                        <Divider orientation="vertical" flexItem sx={{ borderStyle: 'dashed' }} />
                        <Box>
                          <Typography variant="caption" sx={{ display: 'block', color: 'primary.dark', fontWeight: 800, fontSize: '0.55rem' }}>VIDEO AVG</Typography>
                          <Typography variant="subtitle1" sx={{ fontWeight: 900, fontFamily: 'monospace', color: 'primary.main', lineHeight: 1 }}>
                            {perfData.by_generation_type?.VIDEO_COMBINED?.avg_inference_ms ? formatMs(perfData.by_generation_type.VIDEO_COMBINED.avg_inference_ms) : '—'}
                          </Typography>
                        </Box>
                      </Box>
                    ) : (
                      <Typography variant="h5" sx={{ fontWeight: 900, fontFamily: 'monospace', lineHeight: 1 }}>
                        {reportType === 'video' && perfData.by_generation_type?.VIDEO_COMBINED ? formatMs(perfData.by_generation_type.VIDEO_COMBINED.avg_inference_ms) : formatMs(perfData.avg_inference_ms)}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Detailed Row */}
            <Grid container spacing={1.5}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, borderRadius: 2.5, border: '1px solid', borderColor: 'divider', boxShadow: 'none', bgcolor: 'white' }}>
                  <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', mb: 2 }}>Type Breakdown</Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {(reportType === 'all' || reportType === 'image') && perfData.by_generation_type?.IMAGE && (() => {
                      const d = perfData.by_generation_type.IMAGE;
                      const rate = d.count > 0 ? Math.round((d.completed / d.count) * 100) : 0;
                      return (
                        <Box>
                          <Box display="flex" justifyContent="space-between" alignItems="flex-end" mb={0.5}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'success.main' }} />
                              <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.primary' }}>IMAGES <Box component="span" sx={{ color: 'text.disabled' }}>({d.count})</Box></Typography>
                            </Box>
                            <Typography variant="caption" sx={{ fontWeight: 900, fontFamily: 'monospace' }}>{formatMs(d.avg_inference_ms)}</Typography>
                          </Box>
                          <Box sx={{ height: 2, borderRadius: 1, bgcolor: 'grey.100', overflow: 'hidden' }}>
                            <Box sx={{ width: `${rate}%`, height: '100%', bgcolor: 'success.main' }} />
                          </Box>
                        </Box>
                      );
                    })()}

                    {(reportType === 'all' || reportType === 'video') && perfData.by_generation_type?.VIDEO_COMBINED && (() => {
                      const d = perfData.by_generation_type.VIDEO_COMBINED;
                      const s1 = perfData.by_generation_type?.VIDEO_STEP1;
                      const s2 = perfData.by_generation_type?.VIDEO_STEP2;
                      const rate = d.count > 0 ? Math.round((d.completed / d.count) * 100) : 0;
                      return (
                        <Box>
                          <Box display="flex" justifyContent="space-between" alignItems="flex-end" mb={0.5}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.main' }} />
                              <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.primary' }}>VIDEOS <Box component="span" sx={{ color: 'text.disabled' }}>({d.count})</Box></Typography>
                            </Box>
                            <Typography variant="caption" sx={{ fontWeight: 900, fontFamily: 'monospace', color: 'primary.main' }}>{formatMs(d.avg_inference_ms)}</Typography>
                          </Box>
                          <Box sx={{ height: 2, borderRadius: 1, bgcolor: 'primary.50', overflow: 'hidden', mb: 1 }}>
                            <Box sx={{ width: `${rate}%`, height: '100%', bgcolor: 'primary.main' }} />
                          </Box>
                          {s1 && s2 && (
                            <Box sx={{ display: 'flex', gap: 2, opacity: 0.8 }}>
                              <Typography variant="caption" sx={{ fontSize: '0.6rem', fontWeight: 700, color: 'primary.dark' }}>S1: {formatMs(s1.avg_inference_ms)}</Typography>
                              <Typography variant="caption" sx={{ fontSize: '0.6rem', fontWeight: 700, color: 'primary.dark' }}>S2: {formatMs(s2.avg_inference_ms)}</Typography>
                            </Box>
                          )}
                        </Box>
                      );
                    })()}
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, borderRadius: 2.5, border: '1px solid', borderColor: 'divider', boxShadow: 'none', bgcolor: 'white', height: '100%' }}>
                  <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.08em', display: 'block', mb: 1.5 }}>Failure Matrix</Typography>
                  {failureData && failureData.total_failures > 0 ? (
                    <Table size="small" sx={{ '& .MuiTableCell-root': { py: 0.75, px: 0, borderBottom: '1px dashed' } }}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontSize: '0.6rem', fontWeight: 800, color: 'text.disabled', textTransform: 'uppercase' }}>Source</TableCell>
                          <TableCell sx={{ fontSize: '0.6rem', fontWeight: 800, color: 'text.disabled', textTransform: 'uppercase' }}>Signature</TableCell>
                          <TableCell sx={{ fontSize: '0.6rem', fontWeight: 800, color: 'text.disabled', textTransform: 'uppercase' }} align="right">Vol</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {failureData.wavespeed_failures.by_code.map((f, i) => (
                          <TableRow key={`ws-${i}`}>
                            <TableCell><Typography variant="caption" sx={{ fontWeight: 900, color: 'warning.dark', bgcolor: 'warning.50', px: 0.5, borderRadius: 0.5, fontSize: '0.55rem' }}>WAVESP</Typography></TableCell>
                            <TableCell sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'text.secondary' }}>{f.display_name.toUpperCase()}</TableCell>
                            <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 800, fontSize: '0.7rem' }}>{f.count.toString().padStart(2, '0')}</TableCell>
                          </TableRow>
                        ))}
                        {failureData.system_failures.by_code.map((f, i) => (
                          <TableRow key={`sys-${i}`}>
                            <TableCell><Typography variant="caption" sx={{ fontWeight: 900, color: 'text.secondary', bgcolor: 'grey.100', px: 0.5, borderRadius: 0.5, fontSize: '0.55rem' }}>SYSTEM</Typography></TableCell>
                            <TableCell sx={{ fontSize: '0.65rem', fontWeight: 700, color: 'text.secondary' }}>{f.display_name.toUpperCase()}</TableCell>
                            <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 800, fontSize: '0.7rem' }}>{f.count.toString().padStart(2, '0')}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <Box sx={{ height: 100, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', opacity: 0.5 }}>
                      <CheckCircleIcon sx={{ fontSize: 24, color: 'success.main', mb: 1 }} />
                      <Typography variant="caption" sx={{ fontWeight: 800, letterSpacing: '0.1em' }}>NOMINAL STATUS</Typography>
                    </Box>
                  )}
                </Paper>
              </Grid>
            </Grid>

            {/* Insights Row: Edge-to-Edge frame inside the suite */}
            <Box sx={{ position: 'relative', borderRadius: 2.5, overflow: 'hidden', border: '1px solid', borderColor: 'divider', height: 600, bgcolor: 'white' }}>
              <Box sx={{ 
                position: 'absolute', top: 12, right: 12, zIndex: 3,
                display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.5,
                bgcolor: 'rgba(255,255,255,0.9)', borderRadius: 10, border: '1px solid', borderColor: 'grey.200'
              }}>
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'success.main', animation: 'pulse 2s infinite' }} />
                <Typography variant="caption" sx={{ fontWeight: 900, color: 'text.secondary', fontSize: '0.55rem', letterSpacing: '0.05em' }}>ANALYTICS LIVE</Typography>
              </Box>
              
              {metabaseUrl ? (
                <iframe
                  src={getDynamicMetabaseUrl(metabaseUrl)}
                  frameBorder="0" width="100%" height="100%" allowTransparency title="Metabase Analytics"
                  style={{ border: 'none' }}
                />
              ) : (
                <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100%" opacity={0.3}>
                  <InsertChartIcon sx={{ fontSize: 40, mb: 1 }} />
                  <Typography variant="caption" sx={{ fontWeight: 800 }}>DASHBOARD OFFLINE</Typography>
                </Box>
              )}
            </Box>
          </Box>
        ) : (
          <Box display="flex" justifyContent="center" p={4}><Typography color="text.secondary">No generation data available</Typography></Box>
        )}
      </Box>

      {/* Activities and Summary Section */}
      <Box sx={{ mb: { xs: 2.5, sm: 3, md: 3.5 } }}>
        <Typography
          variant="h6"
          gutterBottom
          sx={{
            mb: 2,
            fontWeight: 600,
            color: 'text.primary',
            fontSize: { xs: '1.125rem', sm: '1.25rem' }
          }}
        >
          Recent Activities & Analytics
        </Typography>

        <Grid container spacing={{ xs: 1.5, sm: 2, md: 2.5 }}>
          {/* Left Column - Recent Generation Activities */}
          <Grid item xs={12} lg={6}>
            <Paper
              sx={{
                p: { xs: 1.5, sm: 2 },
                height: '100%',
                minHeight: 300,
                borderRadius: 3,
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                border: '1px solid',
                borderColor: 'divider'
              }}
            >
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: 'text.primary',
                    fontSize: { xs: '1rem', sm: '1.125rem' }
                  }}
                >
                  Recent Generation Activities
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                  Latest AI generation activities across the system
                </Typography>
              </Box>

              {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" p={4}>
                  <Typography color="text.secondary">Loading recent activities...</Typography>
                </Box>
              ) : recentActivities.length === 0 ? (
                <Box display="flex" justifyContent="center" alignItems="center" p={4}>
                  <Typography color="text.secondary">No recent generation activities</Typography>
                </Box>
              ) : (
                <List sx={{ p: 0 }}>
                  {recentActivities.map((activity, index) => (
                    <React.Fragment key={activity.id || index}>
                      <ListItem
                        sx={{
                          px: { xs: 1, sm: 2 },
                          py: { xs: 1.5, sm: 2 },
                          borderRadius: 2,
                          '&:hover': {
                            backgroundColor: 'action.hover',
                            transition: 'background-color 0.2s ease'
                          }
                        }}
                      >
                        <ListItemAvatar>
                          {activity.thumbnail_url ? (
                            <Box
                              sx={{
                                width: { xs: 70, sm: 80 },
                                height: { xs: 70, sm: 80 },
                                borderRadius: 2,
                                cursor: 'pointer',
                                position: 'relative',
                                overflow: 'hidden',
                                border: '2px solid',
                                borderColor: 'divider',
                                transition: 'all 0.2s ease-in-out',
                                '&:hover': {
                                  transform: 'scale(1.05)',
                                  borderColor: 'primary.main',
                                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                                }
                              }}
                              onClick={() => {
                                // Use the activity ID directly as the generation ID
                                const generationId = activity.id;

                                if (generationId) {
                                  if (activity.is_video) {
                                    // For videos, don't pass type parameter - let the backend serve the video file
                                    openMediaPreview(generationId, `Video - ${activity.action}`, true);
                                  } else {
                                    // For images, don't pass type parameter - let the backend serve the image file
                                    openMediaPreview(generationId, `Image - ${activity.action}`, false);
                                  }
                                } else {
                                  console.warn('No generation ID found for activity:', activity);
                                }
                              }}
                            >
                              <SecureImage
                                src={getSecureThumbnailUrl(activity.id, '400x400', activity.is_video ? 'enhanced' : null)}
                                alt={activity.is_video ? "Video thumbnail" : "Image thumbnail"}
                                useThumbnail={true}
                                thumbnailSize="400x400"
                                style={{
                                  width: '100%',
                                  height: '100%',
                                  objectFit: 'cover',
                                  borderRadius: 1
                                }}
                              />
                              {activity.is_video && (
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                    borderRadius: '50%',
                                    width: 32,
                                    height: 32,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'white'
                                  }}
                                >
                                  <PlayArrowIcon sx={{ fontSize: 20 }} />
                                </Box>
                              )}
                            </Box>
                          ) : (
                            <Avatar
                              sx={{
                                width: { xs: 70, sm: 80 },
                                height: { xs: 70, sm: 80 },
                                bgcolor: 'grey.200'
                              }}
                            >
                              {getActivityIcon(activity.action)}
                            </Avatar>
                          )}
                        </ListItemAvatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" sx={{ mb: 0.5 }}>
                            <Typography
                              variant="subtitle1"
                              sx={{
                                fontWeight: 600,
                                fontSize: { xs: '0.875rem', sm: '1rem' }
                              }}
                            >
                              {activity.action} - {activity.module || 'System'}
                            </Typography>
                            {activity.output_format && (
                              <Chip
                                label={activity.output_format}
                                size="small"
                                color={activity.is_video ? 'secondary' : 'primary'}
                                variant="outlined"
                                icon={activity.is_video ? <VideocamIcon /> : <ImageIcon />}
                                sx={{
                                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                  height: { xs: 20, sm: 24 }
                                }}
                              />
                            )}
                          </Box>
                          <Box sx={{ mt: 0.5 }}>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                            >
                              User: {activity.user && activity.user.name ? activity.user.name : 'System'}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                            >
                              {/* Activity timestamps: DD/MM/YYYY, 12-hour time, IST */}
                              {activity.timestamp ? new Date(activity.timestamp).toLocaleString('en-GB', {
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit',
                                second: '2-digit',
                                hour12: true,
                                timeZone: 'Asia/Kolkata'
                              }) : 'N/A'}
                            </Typography>
                          </Box>
                        </Box>
                        <Chip
                          label={activity.success ? 'Success' : 'Failed'}
                          color={activity.success ? 'success' : 'error'}
                          size="small"
                          sx={{
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            height: { xs: 20, sm: 24 },
                            fontWeight: 600
                          }}
                        />
                      </ListItem>
                      {index < recentActivities.length - 1 && (
                        <Divider sx={{ mx: { xs: 1, sm: 2 } }} />
                      )}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </Paper>
          </Grid>

          {/* Right Column - Reports and Generation Summary */}
          <Grid item xs={12} lg={6}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1.5, sm: 2, md: 2.5 } }}>

              {/* Generation Summary */}
              <Paper
                sx={{
                  p: { xs: 1.5, sm: 2 },
                  height: 'auto',
                  minHeight: 200,
                  borderRadius: 3,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  border: '1px solid',
                  borderColor: 'divider',
                  background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)'
                }}
              >
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      color: 'text.primary',
                      fontSize: { xs: '1rem', sm: '1.125rem' }
                    }}
                  >
                    Generation Summary
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                    Today's generation statistics
                  </Typography>
                </Box>

                <Box sx={{ space: 1.5 }}>
                  <Box sx={{
                    p: 1.5,
                    mb: 1.5,
                    borderRadius: 2,
                    bgcolor: 'white',
                    border: '1px solid',
                    borderColor: 'divider'
                  }}>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main', mb: 0.5 }}>
                      {stats.today_videos + stats.today_images}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                      Today's Total Generations
                    </Typography>
                  </Box>

                  <Box sx={{
                    p: 1.5,
                    mb: 1.5,
                    borderRadius: 2,
                    bgcolor: 'white',
                    border: '1px solid',
                    borderColor: 'divider'
                  }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'info.main', mb: 0.5 }}>
                      {stats.today_videos > 0 || stats.today_images > 0 ? Math.round((stats.today_videos / (stats.today_videos + stats.today_images)) * 100) : 0}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                      Video Generation Ratio
                    </Typography>
                  </Box>

                  <Box sx={{
                    p: 1.5,
                    borderRadius: 2,
                    bgcolor: 'white',
                    border: '1px solid',
                    borderColor: 'divider'
                  }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'success.main', mb: 0.5 }}>
                      {stats.today_videos > 0 || stats.today_images > 0 ? Math.round((stats.today_images / (stats.today_videos + stats.today_images)) * 100) : 0}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                      Image Generation Ratio
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Media Preview Dialog */}
      <Dialog
        open={mediaPreview.open}
        onClose={closeMediaPreview}
        maxWidth="md"
        fullWidth={false}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.15)',
            overflow: 'hidden',
            maxWidth: '90vw',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle
          sx={{
            pb: 1.5,
            pt: 1.5,
            px: 2.5,
            background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
            color: 'white',
            borderBottom: '1px solid rgba(255,255,255,0.1)'
          }}
        >
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                backgroundColor: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backdropFilter: 'blur(10px)'
              }}
            >
              {mediaPreview.isVideo ? <VideocamIcon sx={{ fontSize: 18 }} /> : <ImageIcon sx={{ fontSize: 18 }} />}
            </Box>
            <Box>
              <Typography variant="h6" component="span" fontWeight="bold">
                {mediaPreview.title}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9, display: 'block' }}>
                {mediaPreview.isVideo ? 'Video Preview' : 'Image Preview'}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 2, textAlign: 'center' }}>
          {mediaPreview.url && (
            mediaPreview.isVideo ? (
              <SecureVideo
                src={mediaPreview.url}
                poster={getSecureThumbnailUrl(mediaPreview.generationId, '1200x1200', 'enhanced')}
                style={{
                  maxWidth: '100%',
                  maxHeight: '60vh',
                  objectFit: 'contain'
                }}
                controls={true}
              />
            ) : (
              <SecureImage
                src={mediaPreview.url}
                alt={mediaPreview.title}
                style={{
                  maxWidth: '100%',
                  maxHeight: '60vh',
                  objectFit: 'contain'
                }}
              />
            )
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2.5, py: 2, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
          <Button
            onClick={closeMediaPreview}
            variant="outlined"
            size="small"
            sx={{
              borderRadius: 1.5,
              px: 2,
              py: 0.75,
              fontWeight: 600,
              borderColor: '#d1d5db',
              color: '#6b7280',
              '&:hover': {
                borderColor: '#9ca3af',
                backgroundColor: '#f3f4f6'
              }
            }}
          >
            Close
          </Button>
          {mediaPreview.url && (
            <Button
              startIcon={<DownloadIcon />}
              onClick={() => {
                // Extract filename from URL or generate one
                const cleanUrl = mediaPreview.url.split('?')[0];
                const urlParts = cleanUrl.split('/');
                let filename = urlParts[urlParts.length - 1] || 'download';
                
                // Add appropriate extension based on media type if not present
                if (mediaPreview.isVideo && !filename.match(/\.(mp4|webm|mov)$/i)) {
                  filename += '.mp4';
                } else if (!mediaPreview.isVideo && !filename.match(/\.(jpg|jpeg|png|webp)$/i)) {
                  filename += '.jpg';
                }
                
                downloadFile(mediaPreview.url, filename);
              }}
              variant="contained"
              size="small"
              sx={{
                borderRadius: 1.5,
                px: 2,
                py: 0.75,
                fontWeight: 600,
                background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                boxShadow: '0 2px 4px rgba(59, 130, 246, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)',
                  boxShadow: '0 4px 8px rgba(59, 130, 246, 0.4)',
                }
              }}
            >
              Download
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Dashboard;